import { createVuetify } from 'vuetify';
import { aliases as mdiAliases } from 'vuetify/iconsets/mdi';
import { VDateInput } from 'vuetify/labs/VDateInput';
import { VFileUpload } from 'vuetify/labs/VFileUpload';
import { VStepperVertical, VStepperVerticalItem } from 'vuetify/labs/VStepperVertical';
import { fr } from 'vuetify/locale';

import type { IconProps } from 'vuetify';
import '../styles/app.scss';

const remixAliases = {
  ...mdiAliases,
  dropdown: 'ri-arrow-down-s-line',
  radioOff: 'ri-circle-line',
  radioOn: 'ri-radio-button-line',
  checkboxOff: 'ri-checkbox-blank-line',
  checkboxOn: 'ri-checkbox-fill',
};
const remixSet = {
  component: (props: IconProps) => {
    const NuxtIcon = resolveComponent('Icon');
    return h(NuxtIcon, {
      name: props.icon,
      ...props,
    });
  },
};

export default defineNuxtPlugin((app) => {
  const vuetify = createVuetify({
    locale: {
      locale: 'fr',
      messages: { fr },
    },
    theme: {
      defaultTheme: 'light',
      themes: {
        light: {
          dark: false,
          colors: {
            // Primary colors
            'primary': '#eb1b34', // Using Primary/300 as main primary
            'primary-100': '#f8a8b1',
            'primary-200': '#f05164',
            'primary-300': '#eb1b34',
            'primary-400': '#bb1024',
            'primary-500': '#840b1a',
            'primary-alpha-10': 'rgba(240, 81, 100, 0.10)',

            // Secondary colors
            'secondary': '#4d5fb8', // Using Secondary/300 as main secondary
            'secondary-100': '#a6afdb',
            'secondary-200': '#7a87ca',
            'secondary-300': '#4d5fb8',
            'secondary-400': '#39478e',
            'secondary-500': '#202850',
            'secondary-alpha-10': '#f5f6f9',

            // Tertiary colors
            'tertiary': '#0000cc', // Using Tertiary/300 as main tertiary
            'tertiary-100': '#6666ff',
            'tertiary-200': '#191aff',
            'tertiary-300': '#0000cc',
            'tertiary-400': '#00007f',
            'tertiary-500': '#000033',
            'tertiary-alpha-10': '#f0f0ff',
            'tertiary-alpha-50': 'rgba(0, 0, 51, 0.50)',

            // Neutral colors
            'neutral-100': '#ffffff',
            'neutral-200': '#e8e8e8',
            'neutral-300': '#d2d2d2',
            'neutral-400': '#bbbbbb',
            'neutral-500': '#a4a4a4',
            'neutral-600': '#8e8e8e',
            'neutral-700': '#777777',
            'neutral-800': '#606060',
            'neutral-900': '#4a4a4a',
            'neutral-1000': '#333333',
            'neutral-alpha-10': '#e6e6e7',

            // Red colors
            'red-50': '#ffd9dd',
            'red-100': '#fb3748',
            'red-200': '#d00416',
            'red-alpha-10': '#ffebed',

            // Orange colors
            'orange-100': '#ff9a00',
            'orange-200': '#ff7f00',
            'orange-alpha-10': '#fff4e5',

            // Yellow colors
            'yellow-100': '#ffdb43',
            'yellow-200': '#dfb400',
            'yellow-alpha-10': '#fffced',

            // Green colors
            'green-50': '#dff4ea',
            'green-100': '#84ebb4',
            'green-200': '#1a7344',
            'green-alpha-10': '#e4f4ed',

            // Blue colors
            'blue-200': '#2C87FF',
            'blue-alpha-10': '#e5eefb',

            // Semantic color mappings
            'error': '#fb3748',
            'warning': '#ffdb43',
            'success': '#84ebb4',
            'info': '#4d5fb8',

            'capfinances': '#E3596A',
            'predictis': '#38478E',
          },
        },
      },
    },
    icons: {
      defaultSet: 'remix',
      aliases: remixAliases,
      sets: {
        remix: remixSet,
      },
    },
    components: {
      VFileUpload,
      VDateInput,
      VStepperVertical,
      VStepperVerticalItem,
    },
    defaults: {
      VStepperVerticalItem: {
        elevation: 0,
        variant: 'flat',
      },
    },
  });

  app.vueApp.use(vuetify);
});
